/**
 * Whether the data-quality findings on screen still describe the batch's data.
 *
 * Validation used to leave no record of *what it read*, so after an accepted
 * upload or an approved correction the worklist silently described a superseded
 * dataset and nothing said to re-run. The backend now stamps each run with the
 * data state it saw and returns a single `stale` verdict; this renders it.
 *
 * The verdict is never re-derived here. Comparing the two `data_version`s would
 * miss the corrections axis entirely — approving a correction deliberately bumps
 * no `data_version` — and would drift from the transition gate, which refuses
 * the forward edges on the backend's answer.
 */

import { Alert, Button, Group, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { ValidationStatus } from "../api/types";
import { formatPacificDisplay } from "../lib/datetime";
import { DisabledReasonTooltip } from "./DisabledReasonTooltip";
import { RunStageLine } from "./RunStageLine";

/** Human copy per `stale_reasons` code, in the order the backend reports them. */
const REASON_COPY: Record<string, string> = {
  upload_accepted: "an upload was accepted",
  correction_decided: "a correction was approved",
  config_changed: "the configuration changed",
  telemetry_changed: "the meter data behind it changed",
  // `period_advanced` is deliberately absent: its copy carries the day count, so
  // it is rendered from `new_data_days` in `staleReasonSentence` instead of from
  // this map. Falling through to UNKNOWN_REASON is the correct behaviour if the
  // count is ever missing.
};

/** The backend code whose copy is built from a count rather than looked up. */
const REASON_PERIOD_ADVANCED = "period_advanced";

/** Stand-in for a code this build does not know about. */
const UNKNOWN_REASON = "the batch inputs changed";

export function staleReasonSentence(reasons: string[], newDataDays = 0): string {
  if (reasons.length === 0) return "the batch data changed";
  // Unknown codes map to generic copy rather than being dropped. The backend can
  // add an axis ahead of this bundle (it has: `config_changed` arrived after the
  // first cut), and filtering the unknown one out would leave a sentence that is
  // specific but INCOMPLETE — "because an upload was accepted" while concealing a
  // second, equally real cause. Deduped so two unknown codes read once.
  const parts = [
    ...new Set(
      reasons.map((r) =>
        r === REASON_PERIOD_ADVANCED && newDataDays > 0
          ? `${newDataDays} ${newDataDays === 1 ? "day" : "days"} of data arrived after it ran`
          : (REASON_COPY[r] ?? UNKNOWN_REASON),
      ),
    ),
  ];
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * One line describing when validation last ran, and against what.
 *
 * Reads the last COMPLETED run, not the newest one: a re-run that is still in
 * flight or that failed has not replaced the findings currently on screen.
 */
export function LastValidatedText({ status }: { status: ValidationStatus }) {
  if (status.running) {
    // The stage is the whole point of this line: a run takes minutes, and a bare
    // "running" that never changes is indistinguishable from a wedged one.
    return (
      <RunStageLine
        label="Validation running…"
        stage={status.last_run?.stage}
        data-test="validation-running"
      />
    );
  }
  const run = status.last_completed_run;
  const failed = status.last_run?.status === "failed";
  // The run's process went away — almost always a deploy — so nothing will ever
  // finish that row. Reported separately from "failed" because the operator's
  // next move is the same (run it again) but the cause is not their data, and
  // saying "failed" would send them looking for a problem that is not there.
  const abandoned = status.abandoned === true;
  if (!run) {
    // A failed FIRST run has a failed `last_run` and no completed one. Checked
    // before the never-validated copy: `POST /validate` already returned 202, so
    // this status is the only channel that can tell the operator the background
    // attempt died — reporting "never validated" would read as "never attempted".
    if (failed) {
      return (
        <Text size="sm" c="red.7" data-test="validation-run-failed">
          The last validation run failed
          {status.last_run?.last_error ? ` (${status.last_run.last_error})` : ""} — no findings were
          recorded. Run it again.
        </Text>
      );
    }
    if (abandoned) {
      return (
        <Text size="sm" c="orange.8" data-test="validation-run-abandoned">
          The last validation run was interrupted before it finished — no findings were recorded.
          Run it again.
        </Text>
      );
    }
    return (
      <Text size="sm" c="dimmed">
        Never validated — run validation to check this period for data-quality issues.
      </Text>
    );
  }
  const when = run.finished_at ?? run.started_at;
  return (
    <Text size="sm" c="dimmed">
      Last validated {formatPacificDisplay(when)}
      {run.trigger === "sweep" ? " (automatic sweep)" : ""}
      {run.resolved ? ` · ${run.resolved} finding(s) auto-resolved` : ""}
      {failed ? " · a later run failed" : ""}
      {abandoned ? " · a later run was interrupted" : ""}
    </Text>
  );
}

/**
 * The stale banner. Renders nothing when the findings are current — a
 * reassurance box on every batch would train reviewers to ignore the box.
 */
export function ValidationStaleAlert({
  status,
  onRerun,
  rerunLabel = "Re-run validation",
  canRerun = false,
  rerunBlockedReason = null,
}: {
  status: ValidationStatus;
  onRerun?: () => void;
  rerunLabel?: string;
  /** Whether this viewer's role may re-run at all. False hides the button. */
  canRerun?: boolean;
  /**
   * Why re-running is unavailable to a role that otherwise could — in practice,
   * a run already in flight. Disables the button and explains in a tooltip.
   *
   * Kept apart from `canRerun` because the two want different treatment: a role
   * that may never re-run should not be shown a button at all, while a temporary
   * refusal should say so and stay put. This banner is the only prompt to re-run
   * on the page, so dropping it mid-run left the operator reading "re-run
   * validation so the list below reflects the current data" with nothing to
   * click and no hint that the run they started was the reason.
   */
  rerunBlockedReason?: string | null;
}) {
  if (!status.stale) return null;
  const run = status.last_completed_run;
  return (
    <Alert
      color="yellow"
      variant="light"
      icon={<IconAlertTriangle size={16} />}
      title="These findings were checked against earlier data"
      data-test="validation-stale-alert"
    >
      <Text size="sm">
        Validation last ran on data version <strong>{run?.data_version ?? "unknown"}</strong>; the
        batch is now on <strong>{status.current_data_version}</strong> because{" "}
        {staleReasonSentence(status.stale_reasons, status.new_data_days)}. Re-run validation so the
        list below reflects the current data — findings the change resolved will close
        automatically.
      </Text>
      {canRerun && onRerun ? (
        <Group mt="sm">
          <DisabledReasonTooltip reason={rerunBlockedReason} position="bottom-start">
            <Button
              size="xs"
              variant="light"
              color="yellow"
              onClick={onRerun}
              disabled={rerunBlockedReason !== null}
            >
              {rerunLabel}
            </Button>
          </DisabledReasonTooltip>
        </Group>
      ) : null}
    </Alert>
  );
}
